import type { IconName } from "@repo/fika-design-system/icon"
import {
  getLanguageLabel,
  type Locale,
  locales,
  parseLocale,
} from "@repo/fika-shared/src/locale"
import { createEffect, createMemo, createSignal, For } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Checkbox } from "@/designSystem/components/checkbox"
import { Form } from "@/designSystem/components/form"
import { Select } from "@/designSystem/components/select"
import { Text } from "@/designSystem/components/text"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"

export const PublicationLanguageFormFragment = graphql(`
  fragment PublicationLanguageFormFragment on Publication {
    id
    locale
    additionalLanguages
  }
`)

interface PublicationLanguageFormProps {
  publication: FragmentOf<typeof PublicationLanguageFormFragment>
  onAfterSave?: () => void | Promise<void>
  id?: string
  title: string
  iconName: IconName
}

export function PublicationLanguageForm(props: PublicationLanguageFormProps) {
  const { LL } = useI18nContext()
  const publication = createFragment(
    PublicationLanguageFormFragment,
    () => props.publication,
  )

  const UpdatePublicationLanguageMutation = graphql(
    `mutation UpdatePublicationLanguage($input: UpdatePublicationInput!) {
      updateCurrentPublication(input: $input) {
        ... on MutationUpdateCurrentPublicationSuccess {
          data {
            ...PublicationLanguageFormFragment
          }
        }
        ...ErrorFragment
      }
    }`,
    [PublicationLanguageFormFragment, ErrorFragment],
  )
  const [updatePublicationState, updatePublication] =
    createMutationWithErrorHandling(
      UpdatePublicationLanguageMutation,
      "updateCurrentPublication",
    )

  const [locale, setLocale] = createSignal<Locale>(
    parseLocale(publication().locale),
  )
  const [enabledLocales, setEnabledLocales] = createSignal<Set<Locale>>(
    new Set(),
  )

  createEffect(() => {
    const pub = publication()
    const additional = pub.additionalLanguages ?? []
    setEnabledLocales(new Set(additional.map(parseLocale)))
  })

  const alternativeLocales = createMemo(() =>
    locales
      .filter((l: Locale) => l !== locale())
      .sort((a, b) => getLanguageLabel(a).localeCompare(getLanguageLabel(b))),
  )

  function handleLocaleChange(newLocale: string | null) {
    if (!newLocale) return
    const l = parseLocale(newLocale)
    const prev = locale()
    setLocale(l)
    setEnabledLocales(current => {
      const next = new Set(current)
      next.add(prev)
      next.delete(l)
      return next
    })
  }

  function toggleLocale(locale: Locale) {
    setEnabledLocales(prev => {
      const next = new Set(prev)
      if (next.has(locale)) {
        next.delete(locale)
      } else {
        next.add(locale)
      }
      return next
    })
  }

  const allAlternativesEnabled = createMemo(() =>
    alternativeLocales().every(l => enabledLocales().has(l)),
  )

  const isDirty = createMemo(() => {
    const pub = publication()
    if (locale() !== pub.locale) return true
    const current = new Set((pub.additionalLanguages ?? []).map(parseLocale))
    const requested = enabledLocales()
    if (current.size !== requested.size) return true
    for (const l of current) {
      if (!requested.has(l)) return true
    }
    return false
  })

  async function onSubmit() {
    const additionalLanguages = allAlternativesEnabled()
      ? null
      : Array.from(enabledLocales())

    const state = await updatePublication({
      input: { locale: locale(), additionalLanguages },
    })
    if (state.success) {
      toast.success(LL().settings.informationSaved())
      await props.onAfterSave?.()
    }
  }

  return (
    <Form
      title={props.title}
      description={LL().blog.languageDescription()}
      hue="amber"
      iconName={props.iconName}
      onSubmit={onSubmit}
      id={props.id}
    >
      <Select
        hue="amber"
        label={LL().blog.language()}
        options={locales
          .map(l => ({
            value: l,
            label: getLanguageLabel(l),
            disabled: false,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))}
        value={locale}
        onChange={handleLocaleChange}
      />
      <Box gap="12">
        <Box gap="4">
          <Text fontSize="s" fontWeight="500">
            {LL().blog.additionalLanguages()}
          </Text>
          <Text fontSize="s" color="sand700">
            {LL().blog.additionalLanguagesDescription()}
          </Text>
        </Box>
        <For each={alternativeLocales()}>
          {l => (
            <Checkbox
              label={getLanguageLabel(l)}
              hue="amber"
              checked={enabledLocales().has(l)}
              onChange={() => toggleLocale(l)}
              disabled={updatePublicationState.fetching}
            />
          )}
        </For>
      </Box>
      <Form.Save
        hue="amber"
        disabled={!isDirty()}
        active={updatePublicationState.fetching}
      />
    </Form>
  )
}
